import {useCallback, useEffect, useMemo, useState} from 'react';
import {
	ALWAYS_ON_COLUMN_KEYS,
	DEFAULT_VISIBLE_COLUMN_KEYS,
	LG_COLUMNS,
	TOGGLEABLE_COLUMNS,
	type ColumnKey,
} from '../constants/listColumns';

const STORAGE_KEY = 'ticketList.visibleColumns.v1';
const TOGGLEABLE_KEY_SET: ReadonlySet<string> = new Set(TOGGLEABLE_COLUMNS.map(c => c.key));
const ALL_KEYS: ReadonlySet<string> = new Set(LG_COLUMNS.map(c => c.key));

function readPersisted(): Set<ColumnKey> | null {
	if (typeof window === 'undefined') return null;
	try {
		const raw = window.localStorage.getItem(STORAGE_KEY);
		if (!raw) return null;
		const parsed = JSON.parse(raw);
		if (!Array.isArray(parsed)) return null;
		const next = new Set<ColumnKey>(ALWAYS_ON_COLUMN_KEYS);
		for (const k of parsed) {
			if (typeof k === 'string' && ALL_KEYS.has(k)) {
				next.add(k as ColumnKey);
			}
		}
		return next;
	} catch {
		return null;
	}
}

function writePersisted(visible: ReadonlySet<ColumnKey>) {
	if (typeof window === 'undefined') return;
	try {
		const toggleableOn: ColumnKey[] = [];
		for (const k of visible) {
			if (TOGGLEABLE_KEY_SET.has(k)) toggleableOn.push(k);
		}
		window.localStorage.setItem(STORAGE_KEY, JSON.stringify(toggleableOn));
	} catch {
		// localStorage may be disabled or quota-exceeded; silently ignore.
	}
}

export interface UseColumnVisibility {
	visible: ReadonlySet<ColumnKey>;
	isVisible: (key: ColumnKey) => boolean;
	toggle: (key: ColumnKey) => void;
}

export function useColumnVisibility(): UseColumnVisibility {
	const [visible, setVisible] = useState<Set<ColumnKey>>(() => {
		return readPersisted() ?? new Set(DEFAULT_VISIBLE_COLUMN_KEYS);
	});

	useEffect(() => {
		writePersisted(visible);
	}, [visible]);

	const toggle = useCallback((key: ColumnKey) => {
		if (!TOGGLEABLE_KEY_SET.has(key)) return;
		setVisible(prev => {
			const next = new Set(prev);
			if (next.has(key)) next.delete(key);
			else next.add(key);
			return next;
		});
	}, []);

	const isVisible = useCallback((key: ColumnKey) => visible.has(key), [visible]);

	return useMemo(() => ({visible, isVisible, toggle}), [visible, isVisible, toggle]);
}
